/**
 * PWA Resume State — persists navigation context before reloads
 * so the app can restore the user's position instead of going to "/".
 */

const STORAGE_KEY = 'pwa-resume-state';
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface ResumeSnapshot {
  pathname: string;
  search: string;
  hash: string;
  ts: number;
}

/** Save current navigation context to localStorage */
export function saveResumeState(): void {
  const snapshot: ResumeSnapshot = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    ts: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch { /* quota exceeded — ignore */ }
}

/**
 * Consume saved state and return the URL to restore, or null.
 * Clears the snapshot after reading so it's one-shot.
 */
export function consumeResumeState(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;
    const snap: ResumeSnapshot = JSON.parse(raw);
    if (Date.now() - snap.ts > TTL_MS) return null;
    // Don't restore auth route
    if (snap.pathname === '/auth') return null;
    return snap.pathname + snap.search + snap.hash;
  } catch {
    return null;
  }
}

/** Check if running as installed PWA (standalone) */
export function isStandalonePWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}
